import { useState } from "react";
import { Alert, Box, Button, Card, CircularProgress, Typography } from "@mui/material";
import { Download, PictureAsPdf } from "@mui/icons-material";
import DashboardLayout from "app/layout/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/layout/DashboardNavbar";
import useFrecuencia from "./hooks/useFrecuencia";
import HeaderFrecuencia from "./components/HeaderFrecuencia";
import FiltrosFrecuencia from "./components/FiltrosFrecuencia";
import FrecuenciaTable from "./components/FrecuenciaTable";
import ReportCompanyHeader from "./components/ReportCompanyHeader";
import { formatReportDate } from "modules/came/model/frequencyModel";

export default function Frecuencia() {
  const model = useFrecuencia();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const exportReport = async (type) => {
    if (!model.processedData || exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      const exports = await import("./reportExports");
      if (type === "pdf") exports.downloadFrequencyPdf(model.processedData);
      else exports.downloadFrequencyCsv(model.processedData);
    } catch (error) {
      setExportError(error.message || "No se pudo generar la descarga.");
    } finally {
      setExporting(false);
    }
  };
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ py: 3 }}>
        <HeaderFrecuencia
          loading={model.loading}
          onRefresh={model.refresh}
          summary={model.getSummary()}
        >
          <FiltrosFrecuencia
            filters={model.filters}
            ticketTypeOptions={model.ticketTypeOptions}
            onFilterChange={model.updateFilters}
            onReset={model.resetFilters}
            onRefresh={model.applyFilters}
            loading={model.loading}
          />
        </HeaderFrecuencia>
        {model.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {model.error}
          </Alert>
        )}
        {exportError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {exportError}
          </Alert>
        )}
        {model.processedData?.warnings.map((warning) => (
          <Alert key={warning} severity="warning" sx={{ mb: 2 }}>
            {warning} Las descargas se identificarán como preliminares.
          </Alert>
        ))}
        <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
          <ReportCompanyHeader />
          <Box
            sx={{
              p: { xs: 2, sm: 3 },
              pb: 0,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Informe de frecuencia
              </Typography>
              {model.processedData && (
                <Typography variant="body2" color="text.secondary">
                  {formatReportDate(model.appliedFilters.startDate)} 00:00 a{" "}
                  {formatReportDate(model.appliedFilters.endDate)} 23:59 · Hora de Colombia
                </Typography>
              )}
              {model.processedData && (
                <Typography variant="caption" color="text.secondary">
                  Tipos:{" "}
                  {model.appliedFilters.ticketTypes.length
                    ? model.appliedFilters.ticketTypes
                        .map((item) => item.label || item.value || item)
                        .join(", ")
                    : "Todos"}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={exporting ? <CircularProgress size={14} /> : <PictureAsPdf />}
                disabled={model.loading || !model.processedData || exporting}
                onClick={() => exportReport("pdf")}
                sx={{ color: "info.main", borderColor: "info.main" }}
              >
                PDF
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Download />}
                disabled={model.loading || !model.processedData || exporting}
                onClick={() => exportReport("csv")}
                sx={{ color: "info.main", borderColor: "info.main" }}
              >
                CSV
              </Button>
            </Box>
          </Box>
          <FrecuenciaTable data={model.processedData} loading={model.loading} />
        </Card>
      </Box>
    </DashboardLayout>
  );
}
